import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePWA } from '../../contexts/PWAContext';
import { useAuth } from '../../contexts/AuthContext';
import { useBrand } from '../../contexts/BrandContext';
import { PERMISSIONS } from '../../utils/permissions';
import {
  Users,
  Building,
  DollarSign,
  Calendar,
  MessageCircle,
  Bell,
  Settings,
  Menu,
  X,
  Plus,
  Wifi,
  WifiOff,
  Download,
  Upload
} from 'lucide-react';
import { getNetworkStatusColor, createPWAComponentTheme } from '../../utils/brandColors';
import { Z_INDEX } from '../../utils/zIndex';

interface MobileNavigationProps {
  onQuickAction?: (action: string) => void;
}

const MobileNavigation: React.FC<MobileNavigationProps> = ({ onQuickAction }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isOnline, networkStatus } = usePWA();
  const { brandColors } = useBrand();

  const currentPath = location.pathname;

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
    setIsQuickActionsOpen(false);
  }, [currentPath]);

  // Hide if no user logged in
  if (!user) {
    return null;
  }

  // Hide on desktop
  const isMobile = window.innerWidth < 768;
  if (!isMobile) {
    return null;
  }

  // Get theme-aware colors
  const _pwaTheme = brandColors ? createPWAComponentTheme(brandColors) : null;
  const statusColor = getNetworkStatusColor(networkStatus, brandColors || {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    primaryLight: 'rgba(37, 99, 235, 0.15)',
    primaryDark: '#1e40af',
    secondary: '#10b981',
    secondaryHover: '#059669',
    secondaryLight: 'rgba(16, 185, 129, 0.15)',
    accent: '#f59e0b',
    accentHover: '#d97706',
    background: '#ffffff',
    text: '#1f2937',
    textSecondary: '#6b7280',
    textMuted: '#9ca3af',
    success: '#22c55e',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6'
  });

  const navigationItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Building, path: '/' },
    { id: 'members', label: 'Members', icon: Users, path: '/members' },
    { id: 'contributions', label: 'Payments', icon: DollarSign, path: '/contributions' },
    { id: 'events', label: 'Events', icon: Calendar, path: '/events' },
    { id: 'messages', label: 'Messages', icon: MessageCircle, path: '/messages' },
    { id: 'sms', label: 'SMS', icon: Download, path: '/sms' },
    { id: 'settings', label: 'Settings', icon: Settings, path: '/settings' }
  ];

  const quickActions = [
    { id: 'add-member', label: 'Add Member', icon: Plus, color: 'bg-green-500' },
    { id: 'record-payment', label: 'Record Payment', icon: DollarSign, color: 'bg-blue-500' },
    { id: 'create-event', label: 'Create Event', icon: Calendar, color: 'bg-purple-500' },
    { id: 'send-sms', label: 'Send SMS', icon: Download, color: 'bg-orange-500' }
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  const handleQuickAction = (action: string) => {
    onQuickAction?.(action);
    setIsQuickActionsOpen(false);
    setIsOpen(false);
  };

  const getNetworkStatusTextColor = () => {
    if (!isOnline) return 'text-red-500';
    if (networkStatus === 'slow') return 'text-yellow-500';
    return 'text-green-500';
  };

  const getNetworkStatusIcon = () => {
    if (!isOnline) return <WifiOff className="w-4 h-4" />;
    return <Wifi className="w-4 h-4" />;
  };

  return (
    <>
      {/* Mobile Navigation Button */}
      <div className="fixed bottom-4 left-4 block md:hidden" style={{ zIndex: 130 }}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`bg-white/90 backdrop-blur-md shadow-xl rounded-full p-4 text-gray-700 hover:text-gray-900 transition-all duration-200 hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-offset-2 ${brandColors?.primary ? `focus:ring-${brandColors.primary.replace('#', '')}` : 'focus:ring-blue-500'
            }`}
          aria-label="Open navigation"
          title="Open Navigation"
        >
          <Menu className="w-6 h-6" />
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 block md:hidden z-[135]"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Mobile Menu */}
      <div className={`fixed bottom-20 left-4 right-4 block md:hidden rounded-3xl shadow-2xl border backdrop-blur-xl transform transition-all duration-300 ${isOpen ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 scale-95 pointer-events-none'
        }`}
        style={{
          backgroundColor: brandColors?.background ? `${brandColors.background}cc` : 'whitecc',
          borderColor: brandColors?.textMuted || '#e5e7eb',
          zIndex: 140
        }}>

        {/* Header with Network Status */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100"
          style={{ borderColor: brandColors?.textMuted || '#e5e7eb' }}>
          <div className="flex items-center gap-3">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: statusColor }}
            />
            <span
              className={`text-sm font-medium ${getNetworkStatusTextColor()}`}
              style={{ color: brandColors?.text || '#374151' }}
            >
              {getNetworkStatusIcon()} {isOnline ? (networkStatus === 'slow' ? 'Slow' : 'Online') : 'Offline'}
            </span>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-400 hover:text-gray-600"
            title="Close Navigation"
            aria-label="Close navigation"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="p-4 space-y-2">
          {navigationItems.map((item) => {
            const isActive = currentPath === item.path;
            const Icon = item.icon;

            // Check permissions for certain routes
            const hasPermission = (() => {
              switch (item.id) {
                case 'sms':
                  return user?.role === 'admin' || user?.role === 'finance_admin' || user?.role === 'developer';
                case 'settings':
                  return user?.role === 'admin' || user?.role === 'developer';
                default:
                  return true;
              }
            })();

            if (!hasPermission) return null;

            return (
              <button
                key={item.id}
                onClick={() => handleNavigation(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${isActive
                  ? 'hover:bg-gray-50'
                  : 'text-gray-700 hover:bg-gray-50'
                  }`}
                style={{
                  backgroundColor: isActive ? (brandColors?.primaryLight || 'rgba(37, 99, 235, 0.15)') : 'transparent',
                  borderColor: isActive ? (brandColors?.primary || '#2563eb') : 'transparent',
                  color: isActive ? (brandColors?.primaryDark || '#1e40af') : (brandColors?.text || '#374151')
                }}
              >
                <Icon className="w-5 h-5" style={{ color: isActive ? (brandColors?.primary || '#2563eb') : (brandColors?.textSecondary || '#6b7280') }} />
                <span className="font-medium">{item.label}</span>
                {isActive && (
                  <div className="ml-auto w-2 h-2 rounded-full" style={{ backgroundColor: brandColors?.primary || '#2563eb' }} />
                )}
              </button>
            );
          })}
        </nav>

        {/* Quick Actions */}
        <div className="border-t border-gray-100 p-4">
          <button
            onClick={() => setIsQuickActionsOpen(!isQuickActionsOpen)}
            className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <span className="font-medium">Quick Actions</span>
            <Plus className={`w-5 h-5 transition-transform ${isQuickActionsOpen ? 'rotate-45' : ''}`} />
          </button>

          {isQuickActionsOpen && (
            <div className="mt-3 space-y-2">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.id}
                    onClick={() => handleQuickAction(action.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 bg-gray-50/50 backdrop-blur-sm rounded-xl hover:bg-gray-100/50 transition-all duration-200 border border-gray-200/50 hover:border-gray-300/50"
                  >
                    <div className={`w-8 h-8 ${action.color} rounded-lg flex items-center justify-center`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-medium text-gray-700">{action.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* User Info */}
        <div className="border-t border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1">
              <div className="font-medium text-gray-900">{user?.name || 'User'}</div>
              <div className="text-sm text-gray-500 capitalize">{user?.role || 'member'}</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileNavigation;